import type { Backend } from '@freelancer/datastore/core';
import { OrderByDirection } from '@freelancer/datastore/core';
import { isDefined } from '@freelancer/utils';
import type { CustomFieldSearchFilterApi } from 'api-typings/resources/metadata';
import { CustomFieldFilterModifierApi } from 'api-typings/resources/metadata';
import type { CustomFieldValueForSearch } from '../custom-field-info-configurations';
import { transformEnterpriseMetadataValuePayload } from '../custom-field-info-configurations';
import type { SuperuserProjectViewAllProjectsCollection } from './superuser-project-view-all-projects.types';

export function superuserProjectViewAllProjectsBackend(): Backend<SuperuserProjectViewAllProjectsCollection> {
  return {
    defaultOrder: {
      field: 'id',
      direction: OrderByDirection.ASC,
    },
    fetch: (authUid, ids, query, order, resourceGroup) => {
      const enterpriseIds = query?.searchQueryParams?.enterprise_ids as
        | number[]
        | undefined;
      const customFieldValues = query?.searchQueryParams
        ?.customFieldValues as unknown as CustomFieldValueForSearch[];

      return {
        endpoint: `superuser/0.1/projects/all`,
        method: 'POST',
        payload: {
          filter: {
            ...query?.searchQueryParams,
            pool_ids: query?.searchQueryParams?.pool_ids,
            custom_fields:
              enterpriseIds && query?.searchQueryParams?.pool_ids
                ? getCustomFieldsFiltersForSearch(
                    enterpriseIds[0],
                    customFieldValues,
                  )
                : [],
            enterprise_ids: enterpriseIds,
            limit: query?.searchQueryParams?.limit,
            offset: query?.searchQueryParams?.offset,
          },
          projection: {
            result_projection: {
              location_details: true,
            },
          },
        },
      };
    },
    push: undefined,
    set: undefined,
    update: undefined,
    remove: undefined,
  };
}

/**
 * Transforms the custom field values to a filter format
 *
 * @param enterpriseId Enterprise id the custom field belongs to
 * @param customFields Custom fields to transform
 */
export function getCustomFieldsFiltersForSearch(
  enterpriseId?: number,
  customFields?: readonly CustomFieldValueForSearch[],
): readonly CustomFieldSearchFilterApi[] {
  if (customFields === undefined || enterpriseId === undefined) {
    return [];
  }
  const customFieldsTransformed = customFields.map(customField =>
    transformEnterpriseMetadataValuePayload(customField),
  );
  const customFieldsFilters: CustomFieldSearchFilterApi[] =
    customFieldsTransformed
      .map(customField => {
        const customFieldId = customFields.find(
          originalCustomField =>
            originalCustomField.customFieldInfoConfigurationId ===
            customField.enterprise_metadata_field_id,
        )?.customFieldInfoId;

        if (!customFieldId) {
          throw new Error(
            `No corresponding custom field found for custom field value with configuration ID ${customField.enterprise_metadata_field_id}`,
          );
        }

        if (!customField.value) {
          return undefined;
        }

        return {
          enterprise_id: enterpriseId,
          custom_field_id: customFieldId,
          modifier: CustomFieldFilterModifierApi.EQUALS,
          query_value: customField.value,
        };
      })
      .filter(isDefined);

  return customFieldsFilters;
}
